import { useEffect, useState, type FormEvent } from 'react';
import { MarcaIcone } from '@shared/components/ui/MarcaIcone';
import { useNavigate } from 'react-router-dom';
import type { EmailOtpType } from '@supabase/supabase-js';
import { chegouParaRedefinirSenha, supabase } from '@shared/auth/supabase';
import s from './Login.module.css';

/**
 * O LINK COM `token_hash` (gerado pela API, api/supabase_admin.gerar_link).
 *
 * O link antigo era a verificacao do proprio Supabase: um GET de uso
 * unico. A previa do WhatsApp, o antivirus do e-mail -- qualquer coisa
 * que abrisse o link antes da pessoa -- gastava o token, e ela chegava
 * aqui com "link expirado" (15/10/2026). Agora o link traz so o token, e
 * ele e trocado pela sessao quando a pessoa ENVIA a senha: robo de
 * previa baixa esta pagina e nao preenche formulario.
 *
 * Lido uma vez, no carregamento: depois da troca, a URL e limpa.
 */
const busca = typeof window !== 'undefined'
  ? new URLSearchParams(window.location.search) : new URLSearchParams();
const TOKEN_HASH = busca.get('token_hash');
const TIPO_TOKEN = (busca.get('type') ?? 'recovery') as EmailOtpType;
// `para=cadastro`: link de provedor, que segue para completar o cadastro
// mesmo quando o token e de recuperacao (ele ja tinha senha).
const PARA_CADASTRO = busca.get('para') === 'cadastro';

/**
 * Onde o link do convite aterrissa.
 *
 * Quem chega aqui ainda NAO tem senha -- mandar para /entrar seria um
 * beco, porque nao haveria o que digitar. O Supabase ja validou o token
 * do link e criou a sessao antes de redirecionar; falta so definir a
 * senha.
 *
 * Depois disso seguimos para /cadastro, onde ele completa os dados.
 *
 * O LINK DE TROCA DE SENHA (pedido no perfil do cliente) cai aqui tambem:
 * e o mesmo gesto -- sessao pelo link, senha nova --, com outro destino.
 * Quem so trocou a senha ja tem cadastro; volta para o app.
 */
export function DefinirAcesso() {
  const comToken = Boolean(TOKEN_HASH);
  const redefinindo = comToken
    ? TIPO_TOKEN === 'recovery' && !PARA_CADASTRO
    : chegouParaRedefinirSenha;
  // Link com token: o token so vale UMA vez. Se a troca deu certo e a
  // senha falhou depois (senha fraca, rede), a nova tentativa ja tem a
  // sessao -- tentar o token de novo daria "expirado".
  const [verificado, setVerificado] = useState(false);
  const [tokenRecusado, setTokenRecusado] = useState(false);
  const [senha, setSenha] = useState('');
  const [confirma, setConfirma] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [temSessao, setTemSessao] = useState<boolean | null>(null);
  const navegar = useNavigate();

  // O Supabase troca o token da URL por uma sessao (detectSessionInUrl).
  // Isso leva um instante: sem esperar, mostrariamos "link invalido" para
  // um link perfeitamente bom.
  useEffect(() => {
    // Com token, nao ha sessao a esperar: ela nasce no envio da senha.
    if (comToken) return;
    let vivo = true;
    supabase.auth.getSession().then(({ data }) => {
      if (vivo) setTemSessao(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, sessao) => {
      if (vivo && sessao) setTemSessao(true);
    });
    return () => { vivo = false; sub.subscription.unsubscribe(); };
  }, []);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (senha.length < 8) {
      setErro('A senha precisa de pelo menos 8 caracteres.');
      return;
    }
    if (senha !== confirma) {
      setErro('As senhas não conferem.');
      return;
    }
    setEnviando(true);
    if (comToken && !verificado) {
      const { error: invalido } = await supabase.auth.verifyOtp({
        token_hash: TOKEN_HASH!, type: TIPO_TOKEN,
      });
      if (invalido) {
        setEnviando(false);
        setTokenRecusado(true);
        return;
      }
      setVerificado(true);
      // O token ja foi usado: tira-lo da URL evita que um recarregar
      // tente de novo e caia em "expirado".
      window.history.replaceState(null, '', window.location.pathname);
    }
    const { error } = await supabase.auth.updateUser({ password: senha });
    setEnviando(false);
    if (error) {
      setErro(error.message);
      return;
    }
    navegar(redefinindo ? '/' : '/cadastro', { replace: true });
  }

  if (!comToken && temSessao === null) {
    return (
      <div className={`${s.tela} shell`}>
        <div className={s.cartao}>
          <div className={s.sub}>
            {redefinindo ? 'Validando o link…' : 'Validando o convite…'}
          </div>
        </div>
      </div>
    );
  }

  if (comToken ? tokenRecusado : !temSessao) {
    return (
      <div className={`${s.tela} shell`}>
        <div className={s.cartao}>
          <div className={s.marca}>
            <MarcaIcone tamanho={32} className={s.ponto} />
            <span className={s.nome}>Heavy Trade</span>
          </div>
          <h1 className={s.titulo}>Link expirado</h1>
          <div className={s.sub}>
            {/* Link vencido nem sempre traz `type=recovery` (o Supabase
                devolve só o erro), então o texto cobre os dois casos. */}
            {redefinindo
              ? 'Este link não é mais válido. Entre na plataforma e peça outro no seu perfil.'
              : 'Este link não é mais válido. Se era um convite, peça um novo ao '
                + 'administrador; se era a troca de senha, peça outro no seu perfil.'}
          </div>
          <div className={s.rodape}>
            <a href="/entrar">Ir para o login</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${s.tela} shell`}>
      <div className={s.cartao}>
        <div className={s.marca}>
          <MarcaIcone tamanho={32} className={s.ponto} />
          <span className={s.nome}>Heavy Trade</span>
        </div>

        <h1 className={s.titulo}>{redefinindo ? 'Nova senha' : 'Defina sua senha'}</h1>
        <div className={s.sub}>
          {redefinindo
            ? 'Escolha a senha nova. Depois disso você volta para a plataforma.'
            : 'Depois disso você completa o cadastro.'}
        </div>

        <form onSubmit={salvar}>
          <div className={s.campo}>
            <label className={s.rotulo} htmlFor="senha">Senha</label>
            <input id="senha" className={s.entrada} type="password" required
                   autoComplete="new-password" minLength={8}
                   value={senha} onChange={(e) => setSenha(e.target.value)} />
          </div>

          <div className={s.campo}>
            <label className={s.rotulo} htmlFor="confirma">Repita a senha</label>
            <input id="confirma" className={s.entrada} type="password" required
                   autoComplete="new-password"
                   value={confirma} onChange={(e) => setConfirma(e.target.value)} />
          </div>

          <button className={s.botao} type="submit" disabled={enviando}>
            {enviando ? 'Salvando…' : 'Definir senha'}
          </button>
        </form>

        {erro && <div className={s.erro}>{erro}</div>}

        <div className={s.rodape}>Mínimo de 8 caracteres.</div>
      </div>
    </div>
  );
}
